/**
 * Custom language detector for i18next.
 * Implements a functional language detection chain with isolated side effects.
 *
 * Detection priority:
 * 1. User profile language (from session in localStorage)
 * 2. Previously selected language (from i18n_language in localStorage)
 * 3. Browser language (from navigator.language)
 * 4. Fallback to English ('en')
 */

import type { LanguageDetectorModule } from 'i18next';
import { isLanguage, type Language } from './types';

/**
 * Pure function to extract language from user profile stored in session.
 * Reads the session from localStorage and extracts the language preference.
 *
 * @returns The user's preferred language, or null if not found
 */
const getUserProfileLanguage = (): Language | null => {
  try {
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) return null;

    const session = JSON.parse(sessionStr);
    const language = session?.preferredLanguage;

    return isLanguage(language) ? language : null;
  } catch {
    return null;
  }
};

/**
 * Pure function to read previously selected language from localStorage.
 * This is set when the user manually changes language.
 *
 * @returns The stored language, or null if not found
 */
const getLocalStorageLanguage = (): Language | null => {
  try {
    const stored = localStorage.getItem('i18n_language');
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
};

/**
 * Pure function to detect browser language.
 * Extracts the language code from navigator.language (e.g., 'en-US' → 'en').
 *
 * @returns The browser language, or null if not supported
 */
const getBrowserLanguage = (): Language | null => {
  try {
    const browserLang = navigator.language.split('-')[0];
    return isLanguage(browserLang) ? browserLang : null;
  } catch {
    return null;
  }
};

/**
 * Pure function to read the tenant's default language from the stored session.
 * The BFF embeds defaultLanguage on each membership (EL-694).
 *
 * @returns The tenant default language, or null if not available
 */
const getTenantDefaultLanguage = (): Language | null => {
  try {
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) return null;

    const session = JSON.parse(sessionStr);
    // Use the primary tenant's defaultLanguage (first membership or the one
    // matching primaryTenantId).
    const memberships: Array<{ tenantId?: string; defaultLanguage?: string }> =
      session?.memberships ?? [];
    const primaryId: string | undefined = session?.primaryTenantId;

    const primary =
      (primaryId && memberships.find(m => m.tenantId === primaryId)) ||
      memberships[0];

    const lang = primary?.defaultLanguage;
    return isLanguage(lang) ? lang : null;
  } catch {
    return null;
  }
};

/**
 * Pure function to return the fallback language.
 * Always returns 'en' as the default.
 *
 * @returns The fallback language ('en')
 */
const getFallbackLanguage = (): Language => {
  return 'en';
};

/**
 * Pure function to validate if a value is a valid language.
 * Re-exported from types for consistency.
 *
 * @param value - The value to validate
 * @returns True if the value is a valid Language
 */
const isValidLanguage = (value: unknown): value is Language => {
  return isLanguage(value);
};

/**
 * Functional composition: detect language using priority chain.
 * Tries each detector in order and returns the first valid result.
 *
 * Priority:
 * 1. User profile language (from session.preferredLanguage)
 * 2. LocalStorage language (from i18n_language key)
 * 3. Browser language
 * 4. Tenant default language (EL-694 — replaces the hardcoded 'en' fallback)
 * 5. Hardcoded 'en' fallback (only when no tenant context is available)
 *
 * @returns A valid Language (never null due to fallback)
 */
export const detectLanguage = (): Language => {
  // Try each detector in priority order
  const detectors = [
    getUserProfileLanguage,
    getLocalStorageLanguage,
    getBrowserLanguage,
    getTenantDefaultLanguage,
  ];

  for (const detector of detectors) {
    const result = detector();
    if (result !== null) {
      return result;
    }
  }

  // Hardcoded fallback — reached when all detectors return null
  return getFallbackLanguage();
};

/**
 * Custom language detector plugin for i18next.
 * This is the side-effect boundary that integrates with i18next's plugin system.
 *
 * The detector implements i18next's LanguageDetectorModule interface:
 * - type: Module type identifier
 * - detect: Called by i18next to detect the language
 * - cacheUserLanguage: Called when language is changed (saves to localStorage)
 */
export const customLanguageDetector: LanguageDetectorModule = {
  type: 'languageDetector',

  /**
   * Detect function called by i18next to detect the language.
   * Uses the functional composition chain to determine language.
   *
   * @returns The detected language
   */
  detect: (): Language => {
    return detectLanguage();
  },

  /**
   * Cache function called when language is changed.
   * Side effect: Writes to localStorage.
   *
   * @param lng - The language to cache
   */
  cacheUserLanguage: (lng: string): void => {
    if (isValidLanguage(lng)) {
      try {
        localStorage.setItem('i18n_language', lng);
      } catch (error) {
        console.warn('Failed to cache language:', error);
      }
    }
  },
};

/**
 * Internal exports for testing purposes.
 * These pure functions can be tested independently.
 *
 * @internal
 */
export const __internal = {
  getUserProfileLanguage,
  getLocalStorageLanguage,
  getBrowserLanguage,
  getTenantDefaultLanguage,
  getFallbackLanguage,
  isValidLanguage,
};
